import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Alert,
  Platform,
  Modal } from 'react-native'
import { X, Camera as CameraIcon, CreditCard, Banknote, Smartphone, ShoppingCart, Wifi, WifiOff, RefreshCw } from 'lucide-react-native'
import { Camera, CameraView } from 'expo-camera'
import { supabase } from '../lib/supabase'
import { api } from '../lib/api'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
// Stripe Terminal types
interface StripeReader {
  id: string
  label: string
  serial_number: string
  device_type: string
  status: 'online' | 'offline'
}

interface TerminalConnectionStatus {
  connected: boolean
  reader: StripeReader | null
  error: string | null
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketPOS'>
  route: RouteProp<RootStackParamList, 'MarketPOS'>
}

interface CartItem {
  id: string
  title: string
  price: number
  seller_id: string
  seller_name: string
}

type PaymentMethod = 'cash' | 'card' | 'stripe_terminal' | 'mobilepay'

// Stripe Terminal SDK (for web)
let stripeTerminal: any = null
let discoveredReaders: StripeReader[] = []

export default function MarketPOSScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const cameraRef = useRef<CameraView>(null)

  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [scanning, setScanning] = useState(false)
  const [manualInput, setManualInput] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [marketName, setMarketName] = useState('')
  const [lastScanned, setLastScanned] = useState<string | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash')
  const [hasStripeTerminal, setHasStripeTerminal] = useState(false)

  // Terminal states
  const [terminalStatus, setTerminalStatus] = useState<TerminalConnectionStatus>({
    connected: false,
    reader: null,
    error: null })
  const [availableReaders, setAvailableReaders] = useState<StripeReader[]>([])
  const [showReaderModal, setShowReaderModal] = useState(false)
  const [discoveringReaders, setDiscoveringReaders] = useState(false)
  const [terminalPaymentStatus, setTerminalPaymentStatus] = useState<string | null>(null)
  const [stripeConnectAccountId, setStripeConnectAccountId] = useState<string | null>(null)

  // Receipt state
  const [showReceipt, setShowReceipt] = useState(false)
  const [receiptTotal, setReceiptTotal] = useState(0)
  const [receiptNumber, setReceiptNumber] = useState('')
  const [receiptCart, setReceiptCart] = useState<CartItem[]>([])

  useEffect(() => {
    fetchMarket()
    requestCameraPermission()
    if (Platform.OS === 'web') {
      loadStripeTerminalSDK()
    }
  }, [marketId])

  const requestCameraPermission = async () => {
    const { status } = await Camera.requestCameraPermissionsAsync()
    setHasPermission(status === 'granted')
  }

  const fetchMarket = async () => {
    const { data } = await supabase
      .from('flea_markets')
      .select('name, stripe_connect_account_id, stripe_terminal_enabled')
      .eq('id', marketId)
      .single()

    if (data) {
      setMarketName(data.name)
      setHasStripeTerminal(!!data.stripe_terminal_enabled && !!data.stripe_connect_account_id)
      setStripeConnectAccountId(data.stripe_connect_account_id)
    }
  }

  // Load Stripe Terminal SDK for web
  const loadStripeTerminalSDK = async () => {
    if (typeof window === 'undefined') return

    // Check if already loaded
    if ((window as any).StripeTerminal) {
      initializeTerminal()
      return
    }

    // Load the script
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/terminal/v1/'
    script.async = true
    script.onload = () => initializeTerminal()
    document.body.appendChild(script)
  }

  // Initialize Stripe Terminal
  const initializeTerminal = async () => {
    if (!(window as any).StripeTerminal) {
      console.error('Stripe Terminal SDK not loaded')
      return
    }

    try {
      stripeTerminal = (window as any).StripeTerminal.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: handleReaderDisconnect })
      console.log('Stripe Terminal initialized')
    } catch (error: any) {
      console.error('Failed to initialize Terminal:', error)
      setTerminalStatus(prev => ({ ...prev, error: error.message }))
    }
  }

  // Fetch connection token from backend
  const fetchConnectionToken = async (): Promise<string> => {
    try {
      const data = await api.createTerminalConnectionToken.fetch({ market_id: marketId })
      return (data as any).secret
    } catch (error: any) {
      console.error('Error fetching connection token:', error)
      throw error
    }
  }

  // Handle unexpected reader disconnect
  const handleReaderDisconnect = () => {
    setTerminalStatus({
      connected: false,
      reader: null,
      error: t('reader_disconnected') || 'Reader disconnected unexpectedly' })
    setPaymentMethod('cash') // Fall back to cash
  }

  // Discover available readers
  const discoverReaders = async () => {
    if (!stripeTerminal) {
      showAlert(t('error') || 'Error', t('terminal_not_ready') || 'Terminal SDK not ready')
      return
    }

    setDiscoveringReaders(true)
    setAvailableReaders([])

    try {
      const discoverResult = await stripeTerminal.discoverReaders({
        simulated: process.env.NODE_ENV === 'development', // Use simulated readers in dev
      })

      if (discoverResult.error) {
        throw new Error(discoverResult.error.message)
      }

      const readers = discoverResult.discoveredReaders || []
      setAvailableReaders(readers.map((r: any) => ({
        id: r.id,
        label: r.label || r.serial_number,
        serial_number: r.serial_number,
        device_type: r.device_type,
        status: r.status === 'online' ? 'online' : 'offline' })))
      discoveredReaders = readers

      if (readers.length === 0) {
        showAlert(t('info') || 'Info', t('no_readers_found') || 'No readers found. Make sure your reader is powered on and connected to the same network.')
      } else {
        setShowReaderModal(true)
      }
    } catch (error: any) {
      console.error('Error discovering readers:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setDiscoveringReaders(false)
    }
  }

  // Connect to a reader
  const connectToReader = async (readerId: string) => {
    if (!stripeTerminal) return

    const reader = discoveredReaders.find((r: any) => r.id === readerId)
    if (!reader) return

    try {
      const connectResult = await stripeTerminal.connectReader(reader)

      if (connectResult.error) {
        throw new Error(connectResult.error.message)
      }

      setTerminalStatus({
        connected: true,
        reader: {
          id: reader.id,
          label: reader.label || reader.serial_number,
          serial_number: reader.serial_number,
          device_type: reader.device_type,
          status: 'online' },
        error: null })
      setShowReaderModal(false)
      showAlert(t('success') || 'Success', t('reader_connected') || 'Reader connected successfully')
    } catch (error: any) {
      console.error('Error connecting to reader:', error)
      setTerminalStatus(prev => ({ ...prev, error: error.message }))
      showAlert(t('error') || 'Error', error.message)
    }
  }

  // Disconnect from reader
  const disconnectReader = async () => {
    if (!stripeTerminal) return

    try {
      await stripeTerminal.disconnectReader()
      setTerminalStatus({
        connected: false,
        reader: null,
        error: null })
    } catch (error: any) {
      console.error('Error disconnecting reader:', error)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const handleBarCodeScanned = async ({ data }: { data: string }) => {
    // Prevent duplicate scans
    if (data === lastScanned) return
    setLastScanned(data)

    // Reset after a delay to allow re-scanning the same item
    setTimeout(() => setLastScanned(null), 2000)

    await addProductToCart(data)
  }

  const addProductToCart = async (productIdOrBarcode: string) => {
    setLoading(true)
    try {
      // Try to find product by ID or barcode
      let query = supabase
        .from('products')
        .select(`
          id,
          title,
          title_fi,
          title_en,
          price,
          status,
          seller_id,
          seller:users!products_seller_id_fkey (
            full_name
          )
        `)
        .eq('flea_market_id', marketId)
        .eq('status', 'for_sale')

      // Check if it looks like a UUID
      if (productIdOrBarcode.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        query = query.eq('id', productIdOrBarcode)
      } else {
        query = query.eq('barcode', productIdOrBarcode)
      }

      const { data: product, error } = await query.single()

      if (error || !product) {
        showAlert(t('error') || 'Error', t('product_not_found') || 'Product not found')
        return
      }

      // Check if already in cart
      if (cart.some(item => item.id === product.id)) {
        showAlert(t('info') || 'Info', t('already_in_cart') || 'This item is already in the cart')
        return
      }

      const seller = Array.isArray(product.seller) ? product.seller[0] : product.seller

      setCart(prev => [...prev, {
        id: product.id,
        title: product.title_fi || product.title_en || product.title || 'Unknown',
        price: product.price || 0,
        seller_id: product.seller_id,
        seller_name: seller?.full_name || 'Unknown Seller' }])

    } catch (error: any) {
      console.error('Error adding product:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to add product')
    } finally {
      setLoading(false)
    }
  }

  const handleManualAdd = () => {
    if (!manualInput.trim()) return
    addProductToCart(manualInput.trim())
    setManualInput('')
  }

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.id !== productId))
  }

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + item.price, 0)
  }

  const processStripeTerminalPayment = async (amount: number): Promise<boolean> => {
    // Check if terminal is connected
    if (!stripeTerminal || !terminalStatus.connected) {
      showAlert(
        t('error') || 'Error',
        t('no_reader_connected') || 'No card reader connected. Please connect a reader first.'
      )
      return false
    }

    try {
      setTerminalPaymentStatus(t('creating_payment') || 'Creating payment...')

      // 1. Create a PaymentIntent on the backend
      const result = await api.createTerminalPayment.fetch({
        market_id: marketId,
        amount: Math.round(amount * 100), // Convert to cents
      }) as any

      setTerminalPaymentStatus(t('present_card') || 'Please present card on the terminal...')

      // 2. Collect payment method using the reader
      const collectResult = await stripeTerminal.collectPaymentMethod(result.client_secret)

      if (collectResult.error) {
        throw new Error(collectResult.error.message)
      }

      setTerminalPaymentStatus(t('processing_payment') || 'Processing payment...')

      // 3. Confirm the payment
      const confirmResult = await stripeTerminal.processPayment(collectResult.paymentIntent)

      if (confirmResult.error) {
        throw new Error(confirmResult.error.message)
      }

      // 4. Check payment status
      if (confirmResult.paymentIntent.status === 'succeeded') {
        setTerminalPaymentStatus(null)
        return true
      } else if (confirmResult.paymentIntent.status === 'requires_capture') {
        // Payment authorized, needs capture (for auth-and-capture flow)
        // In most cases with automatic capture, this won't happen
        setTerminalPaymentStatus(null)
        return true
      } else {
        throw new Error(`Payment failed with status: ${confirmResult.paymentIntent.status}`)
      }
    } catch (error: any) {
      console.error('Terminal payment error:', error)
      setTerminalPaymentStatus(null)

      // Check if user cancelled
      if (error.message?.includes('canceled') || error.code === 'canceled') {
        showAlert(t('cancelled') || 'Cancelled', t('payment_cancelled') || 'Payment was cancelled')
      } else {
        showAlert(t('error') || 'Error', error.message || 'Terminal payment failed')
      }
      return false
    }
  }

  // Cancel ongoing terminal payment
  const cancelTerminalPayment = async () => {
    if (!stripeTerminal) return

    try {
      await stripeTerminal.cancelCollectPaymentMethod()
      setTerminalPaymentStatus(null)
    } catch (error: any) {
      console.error('Error cancelling payment:', error)
    }
  }

  const generateReceiptNumber = () => {
    const now = new Date()
    const date = now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0')
    const seq = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0')
    const rand = String(Math.floor(Math.random() * 100)).padStart(2, '0')
    return `R-${date}-${seq}${rand}`
  }

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      showAlert(t('info') || 'Info', t('cart_empty') || 'Cart is empty')
      return
    }

    setProcessing(true)
    try {
      // If using Stripe terminal, process payment first
      if (paymentMethod === 'stripe_terminal') {
        const paymentSuccess = await processStripeTerminalPayment(getTotal())
        if (!paymentSuccess) {
          setProcessing(false)
          return
        }
      }

      const total = getTotal()
      const rNumber = generateReceiptNumber()

      // 1. Create sale record with correct columns
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .insert({
          flea_market_id: marketId,
          subtotal: total,
          discount_amount: 0,
          total,
          receipt_number: rNumber })
        .select('id')
        .single()

      if (saleError) throw saleError

      // 2. Create sale items + mark products sold
      for (const item of cart) {
        const { error: itemError } = await supabase.from('sale_items').insert({
          sale_id: sale.id,
          product_id: item.id,
          price: item.price,
          quantity: 1,
          seller_id: item.seller_id,
          commission_percentage: 0,
          commission_amount: 0,
          seller_amount: item.price })
        if (itemError) throw itemError

        const { error: soldError } = await supabase
          .from('products')
          .update({ status: 'sold', sold_at: new Date().toISOString() })
          .eq('id', item.id)
        if (soldError) console.error('Error marking product sold:', soldError)
      }

      // 3. Record payment
      const pmMethod = paymentMethod === 'stripe_terminal' ? 'card' : paymentMethod
      await supabase.from('sale_payments').insert({
        sale_id: sale.id,
        payment_method: pmMethod,
        amount: total,
        status: 'completed' })

      // 4. Show receipt
      setReceiptCart([...cart])
      setReceiptTotal(total)
      setReceiptNumber(rNumber)
      setCart([])
      setShowReceipt(true)

    } catch (error: any) {
      console.error('Error completing sale:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to complete sale')
    } finally {
      setProcessing(false)
    }
  }

  // Render reader selection modal
  const renderReaderModal = () => (
    <Modal
      visible={showReaderModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowReaderModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_reader') || 'Select Card Reader'}</Text>
            <TouchableOpacity onPress={() => setShowReaderModal(false)}>
              <X size={24} color={theme.colors.text.primary} />
            </TouchableOpacity>
          </View>

          {availableReaders.length === 0 ? (
            <View style={styles.emptyReaders}>
              <Text style={styles.emptyReadersText}>
                {t('no_readers_found') || 'No readers found'}
              </Text>
            </View>
          ) : (
            <ScrollView style={styles.readerList}>
              {availableReaders.map((reader) => (
                <TouchableOpacity
                  key={reader.id}
                  style={styles.readerItem}
                  onPress={() => connectToReader(reader.id)}
                >
                  <View style={styles.readerInfo}>
                    <Text style={styles.readerName}>{reader.label}</Text>
                    <Text style={styles.readerSerial}>{reader.serial_number}</Text>
                    <Text style={styles.readerType}>{reader.device_type}</Text>
                  </View>
                  <View style={[
                    styles.readerStatus,
                    reader.status === 'online' ? styles.readerOnline : styles.readerOffline
                  ]}>
                    {reader.status === 'online' ? (
                      <Wifi size={16} color="#4CAF50" />
                    ) : (
                      <WifiOff size={16} color="#9E9E9E" />
                    )}
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}

          <TouchableOpacity
            style={styles.discoverButton}
            onPress={discoverReaders}
            disabled={discoveringReaders}
          >
            {discoveringReaders ? (
              <Spinner color="#fff" size="small" />
            ) : (
              <>
                <RefreshCw size={18} color="#fff" />
                <Text style={styles.discoverButtonText}>
                  {t('refresh_readers') || 'Refresh Readers'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  )

  // Render terminal payment status overlay
  const renderPaymentStatusOverlay = () => {
    if (!terminalPaymentStatus) return null

    return (
      <View style={styles.paymentStatusOverlay}>
        <View style={styles.paymentStatusCard}>
          <Spinner size="large" />
          <Text style={styles.paymentStatusText}>{terminalPaymentStatus}</Text>
          <TouchableOpacity
            style={styles.cancelPaymentButton}
            onPress={cancelTerminalPayment}
          >
            <Text style={styles.cancelPaymentText}>{t('cancel') || 'Cancel'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    )
  }

  const renderReceiptModal = () => (
    <Modal
      visible={showReceipt}
      transparent
      animationType="fade"
      onRequestClose={() => setShowReceipt(false)}
    >
      <View style={styles.receiptOverlay}>
        <View style={styles.receiptModal}>
          {/* Header */}
          <View style={styles.receiptHeader}>
            <Text style={styles.receiptStoreName}>{marketName}</Text>
            <Text style={styles.receiptNumber}>{receiptNumber}</Text>
          </View>

          {/* Items */}
          <ScrollView style={styles.receiptItemList} showsVerticalScrollIndicator={false}>
            {receiptCart.map((item, i) => (
              <View key={item.id} style={[styles.receiptItemRow, i > 0 && { borderTopWidth: 1, borderTopColor: '#f0f0f0' }]}>
                <Text style={styles.receiptItemName} numberOfLines={2}>{item.title}</Text>
                <Text style={styles.receiptItemSeller}>{item.seller_name}</Text>
                <Text style={styles.receiptItemPrice}>{item.price.toFixed(2)} €</Text>
              </View>
            ))}
          </ScrollView>

          {/* Total */}
          <View style={styles.receiptTotal}>
            <Text style={styles.receiptTotalLabel}>{t('total') || 'Total'}</Text>
            <Text style={styles.receiptTotalAmount}>{receiptTotal.toFixed(2)} €</Text>
          </View>

          {/* Close */}
          <TouchableOpacity style={styles.receiptCloseBtn} onPress={() => setShowReceipt(false)}>
            <Text style={styles.receiptCloseBtnText}>{t('done') || 'Valmis'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  )

  return (
    <View style={styles.container}>
      {renderReaderModal()}
      {renderPaymentStatusOverlay()}
      {renderReceiptModal()}

      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('point_of_sale') || 'Point of Sale'}</Text>
        <View style={styles.headerRow}>
          <Text style={styles.headerSubtitle}>{marketName}</Text>
          {hasStripeTerminal && Platform.OS === 'web' && (
            <TouchableOpacity
              style={[
                styles.terminalStatusBadge,
                terminalStatus.connected ? styles.terminalConnected : styles.terminalDisconnected
              ]}
              onPress={terminalStatus.connected ? disconnectReader : discoverReaders}
              disabled={discoveringReaders}
            >
              {discoveringReaders ? (
                <Spinner size="small" />
              ) : terminalStatus.connected ? (
                <>
                  <Wifi size={14} color="#fff" />
                  <Text style={styles.terminalStatusText}>
                    {terminalStatus.reader?.label || t('connected') || 'Connected'}
                  </Text>
                </>
              ) : (
                <>
                  <WifiOff size={14} color="#fff" />
                  <Text style={styles.terminalStatusText}>
                    {t('connect_reader') || 'Connect Reader'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.content}>
        {/* Scanner Section */}
        <View style={styles.scannerSection}>
          {scanning && hasPermission ? (
            <View style={styles.cameraContainer}>
              <CameraView
                ref={cameraRef}
                style={styles.camera}
                barcodeScannerSettings={{
                  barcodeTypes: ['qr', 'ean13', 'ean8', 'code128', 'code39'] }}
                onBarcodeScanned={handleBarCodeScanned}
              />
              <TouchableOpacity
                style={styles.closeScannerButton}
                onPress={() => setScanning(false)}
              >
                <X size={24} color="#fff" />
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.scanActions}>
              <TouchableOpacity
                style={styles.scanButton}
                onPress={() => setScanning(true)}
                disabled={!hasPermission}
              >
                <CameraIcon size={24} color="#fff" />
                <Text style={styles.scanButtonText}>
                  {t('scan_barcode') || 'Scan Barcode'}
                </Text>
              </TouchableOpacity>

              <View style={styles.manualInputContainer}>
                <TextInput
                  style={styles.manualInput}
                  value={manualInput}
                  onChangeText={setManualInput}
                  placeholder={t('enter_product_id') || 'Enter product ID or barcode'}
                  placeholderTextColor={theme.colors.text.secondary}
                  onSubmitEditing={handleManualAdd}
                />
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={handleManualAdd}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner color="#fff" size="small" />
                  ) : (
                    <Text style={styles.addButtonText}>+</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>

        {/* Cart Section */}
        <View style={styles.cartSection}>
          <Text style={styles.cartTitle}>
            {t('cart') || 'Cart'} ({cart.length})
          </Text>

          {cart.length === 0 ? (
            <View style={styles.emptyCart}>
              <ShoppingCart size={32} color={theme.colors.text.secondary} />
              <Text style={styles.emptyCartText}>
                {t('scan_to_add') || 'Scan items to add to cart'}
              </Text>
            </View>
          ) : (
            <ScrollView style={styles.cartList}>
              {cart.map((item) => (
                <View key={item.id} style={styles.cartItem}>
                  <View style={styles.cartItemInfo}>
                    <Text style={styles.cartItemTitle} numberOfLines={1}>
                      {item.title}
                    </Text>
                    <Text style={styles.cartItemSeller}>
                      {item.seller_name}
                    </Text>
                  </View>
                  <Text style={styles.cartItemPrice}>
                    €{item.price.toFixed(2)}
                  </Text>
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeFromCart(item.id)}
                  >
                    <X size={16} color="#F44336" />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          )}
        </View>

        {/* Total & Checkout */}
        <View style={styles.checkoutSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('total') || 'Total'}</Text>
            <Text style={styles.totalAmount}>€{getTotal().toFixed(2)}</Text>
          </View>

          {/* Payment Method Selection */}
          <View style={styles.paymentMethodSection}>
            <Text style={styles.paymentMethodLabel}>
              {t('payment_method') || 'Payment Method'}
            </Text>
            <View style={styles.paymentMethodButtons}>
              <TouchableOpacity
                style={[
                  styles.paymentMethodButton,
                  paymentMethod === 'cash' && styles.paymentMethodButtonActive,
                ]}
                onPress={() => setPaymentMethod('cash')}
              >
                <Banknote size={20} color={paymentMethod === 'cash' ? '#fff' : theme.colors.text.secondary} />
                <Text style={[
                  styles.paymentMethodText,
                  paymentMethod === 'cash' && styles.paymentMethodTextActive,
                ]}>
                  {t('cash') || 'Cash'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.paymentMethodButton,
                  paymentMethod === 'card' && styles.paymentMethodButtonActive,
                ]}
                onPress={() => setPaymentMethod('card')}
              >
                <CreditCard size={20} color={paymentMethod === 'card' ? '#fff' : theme.colors.text.secondary} />
                <Text style={[
                  styles.paymentMethodText,
                  paymentMethod === 'card' && styles.paymentMethodTextActive,
                ]}>
                  {t('card_external') || 'Card'}
                </Text>
              </TouchableOpacity>

              {hasStripeTerminal && (
                <TouchableOpacity
                  style={[
                    styles.paymentMethodButton,
                    paymentMethod === 'stripe_terminal' && styles.paymentMethodButtonActive,
                  ]}
                  onPress={() => setPaymentMethod('stripe_terminal')}
                >
                  <Smartphone size={20} color={paymentMethod === 'stripe_terminal' ? '#fff' : theme.colors.text.secondary} />
                  <Text style={[
                    styles.paymentMethodText,
                    paymentMethod === 'stripe_terminal' && styles.paymentMethodTextActive,
                  ]}>
                    {t('stripe_terminal') || 'Stripe'}
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[
                  styles.paymentMethodButton,
                  paymentMethod === 'mobilepay' && styles.paymentMethodButtonActive,
                ]}
                onPress={() => setPaymentMethod('mobilepay')}
              >
                <Text style={styles.paymentMethodIcon}>📲</Text>
                <Text style={[
                  styles.paymentMethodText,
                  paymentMethod === 'mobilepay' && styles.paymentMethodTextActive,
                ]}>
                  MobilePay
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <TouchableOpacity
            style={[
              styles.checkoutButton,
              (processing || cart.length === 0) && styles.checkoutButtonDisabled,
            ]}
            onPress={handleCompleteSale}
            disabled={processing || cart.length === 0}
          >
            {processing ? (
              <Spinner color="#fff" />
            ) : (
              <Text style={styles.checkoutButtonText}>
                {paymentMethod === 'stripe_terminal'
                  ? (t('charge_card') || 'Charge Card')
                  : (t('complete_sale') || 'Complete Sale')}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  content: {
    flex: 1,
    padding: 16 },
  scannerSection: {
    marginBottom: 16 },
  cameraContainer: {
    height: 200,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden',
    position: 'relative' },
  camera: {
    flex: 1 },
  closeScannerButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center' },
  closeScannerText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold' },
  scanActions: {
    gap: 12 },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.merchant.primary,
    padding: 16,
    borderRadius: theme.borderRadius.md,
    gap: 10 },
  scanButtonIcon: {
    fontSize: 24 },
  scanButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600' },
  manualInputContainer: {
    flexDirection: 'row',
    gap: 10 },
  manualInput: {
    flex: 1,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  addButton: {
    width: 50,
    backgroundColor: theme.colors.merchant.primary,
    borderRadius: theme.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center' },
  addButtonText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold' },
  cartSection: {
    flex: 1,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 16 },
  cartTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 12 },
  emptyCart: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40 },
  emptyCartIcon: {
    fontSize: 48,
    marginBottom: 12,
    opacity: 0.5 },
  emptyCartText: {
    fontSize: 16,
    color: theme.colors.text.secondary },
  cartList: {
    flex: 1 },
  cartItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    padding: 12,
    borderRadius: theme.borderRadius.md,
    marginBottom: 8 },
  cartItemInfo: {
    flex: 1 },
  cartItemTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.text.primary },
  cartItemSeller: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  cartItemPrice: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.merchant.primary,
    marginHorizontal: 12 },
  removeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#ffebee',
    justifyContent: 'center',
    alignItems: 'center' },
  removeButtonText: {
    color: '#f44336',
    fontSize: 14,
    fontWeight: 'bold' },
  checkoutSection: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 80 },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16 },
  totalLabel: {
    fontSize: 18,
    color: theme.colors.text.secondary },
  totalAmount: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  paymentMethodSection: {
    marginBottom: 16 },
  paymentMethodLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.secondary,
    marginBottom: 8 },
  paymentMethodButtons: {
    flexDirection: 'row',
    gap: 8 },
  paymentMethodButton: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 2,
    borderColor: 'transparent' },
  paymentMethodButtonActive: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4CAF50' },
  paymentMethodIcon: {
    fontSize: 20,
    marginBottom: 4 },
  paymentMethodText: {
    fontSize: 12,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  paymentMethodTextActive: {
    color: '#4CAF50',
    fontWeight: '600' },
  checkoutButton: {
    backgroundColor: '#4CAF50',
    padding: 18,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center' },
  checkoutButtonDisabled: {
    opacity: 0.5 },
  checkoutButtonText: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '600' },
  // Header row for terminal status
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4 },
  terminalStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 6 },
  terminalConnected: {
    backgroundColor: '#4CAF50' },
  terminalDisconnected: {
    backgroundColor: '#9E9E9E' },
  terminalStatusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '70%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20 },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text.primary },
  readerList: {
    maxHeight: 300 },
  readerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.background.secondary,
    padding: 16,
    borderRadius: theme.borderRadius.md,
    marginBottom: 10 },
  readerInfo: {
    flex: 1 },
  readerName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  readerSerial: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  readerType: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2,
    textTransform: 'capitalize' },
  readerStatus: {
    padding: 8,
    borderRadius: 20 },
  readerOnline: {
    backgroundColor: '#E8F5E9' },
  readerOffline: {
    backgroundColor: '#F5F5F5' },
  emptyReaders: {
    padding: 40,
    alignItems: 'center' },
  emptyReadersText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  discoverButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.merchant.primary,
    padding: 14,
    borderRadius: theme.borderRadius.md,
    marginTop: 16,
    gap: 8 },
  discoverButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  // Payment status overlay
  paymentStatusOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000 },
  paymentStatusCard: {
    backgroundColor: theme.colors.background.card,
    padding: 32,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    width: '80%',
    maxWidth: 300 },
  paymentStatusText: {
    fontSize: 18,
    fontWeight: '500',
    color: theme.colors.text.primary,
    marginTop: 20,
    textAlign: 'center' },
  cancelPaymentButton: {
    marginTop: 24,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    backgroundColor: '#F44336' },
  cancelPaymentText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },

  // Receipt modal
  receiptOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24 },
  receiptModal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    overflow: 'hidden' },
  receiptHeader: {
    padding: 20,
    backgroundColor: '#2d5a27',
    alignItems: 'center' },
  receiptStoreName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 4 },
  receiptNumber: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' },
  receiptItemList: {
    maxHeight: 320,
    paddingHorizontal: 20 },
  receiptItemRow: {
    paddingVertical: 10 },
  receiptItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2 },
  receiptItemSeller: {
    fontSize: 12,
    color: '#888',
    marginBottom: 4 },
  receiptItemPrice: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2d5a27' },
  receiptTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderTopWidth: 2,
    borderTopColor: '#eee' },
  receiptTotalLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a1a1a' },
  receiptTotalAmount: {
    fontSize: 22,
    fontWeight: '800',
    color: '#2d5a27' },
  receiptCloseBtn: {
    margin: 16,
    marginTop: 0,
    backgroundColor: '#2d5a27',
    borderRadius: 10,
    padding: 14,
    alignItems: 'center' },
  receiptCloseBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700' } })
